import { useEffect, useState } from "react";
import { Button, Icon } from "semantic-ui-react";

import { CarNoteModal, DetailsCard, useCan } from "components";
import { DMSNotes, NoteRow } from "components/Notes";
import { CAR_NOTE_TYPE } from "models";
import "modules/CarDetails/components/CarNotes/CarNotes.scss";
import { useCarLocation } from "modules/CarDetails/hooks";

import type { SectionMenu } from "components";
import type { Car, CarNote } from "models";

type CarNotesProps = {
  car: Car;
  sectionMenu: SectionMenu;
  isExpanded: boolean;
};

const getCarNotes = (car: Car) => ({
  dmsCarNotes: car.notes?.filter(note => note.car_note_type_id === CAR_NOTE_TYPE.DMS) ?? [],
  defaultCarNotes: car.notes?.filter(note => note.car_note_type_id === CAR_NOTE_TYPE.Default) ?? []
});

export const getCarNotesCount = (car?: Car) => {
  if (!car) return 0;

  return Object.values(getCarNotes(car)).reduce((count, notes) => count + notes.length, 0);
};

export const CarNotes = ({ car, sectionMenu, isExpanded }: CarNotesProps) => {
  const carLocation = useCarLocation(car);

  const [showCardDetails, setShowCardDetails] = useState(isExpanded);
  const [selectedCarNote, setSelectedCarNote] = useState<CarNote | null>(null);
  const [noteType, setNoteType] = useState<CAR_NOTE_TYPE | null>(null);
  const canCreateNotes = useCan("create", "car-notes");

  useEffect(() => {
    setShowCardDetails(isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    if (!noteType) setNoteType(null);
    else if (noteType === CAR_NOTE_TYPE.Default) {
      const note = car.notes?.find(note => note.car_note_type_id === noteType);
      if (note) setSelectedCarNote(note);
    }
  }, [noteType, car]);

  const toggleCardDetails = () => {
    setShowCardDetails(prevState => !prevState);
  };

  const handleSelectCarNote = (note: CarNote | null) => {
    setNoteType(note?.car_note_type_id || null);
    setSelectedCarNote(note);
  };

  const handleUnselectNote = () => {
    setSelectedCarNote(null);
    setNoteType(null);
  };

  const { dmsCarNotes, defaultCarNotes } = getCarNotes(car);

  return (
    <DetailsCard
      sectionMenuId={sectionMenu.id}
      title={sectionMenu.count ? `${sectionMenu.title} (${sectionMenu.count})` : sectionMenu.title}
      icon={sectionMenu.icon}
      showExpandArrow={false}
      rightContent={
        canCreateNotes &&
        defaultCarNotes.length < 1 && (
          <>
            <Icon className={`chevron ${showCardDetails ? "down" : "right"} pointer`} onClick={toggleCardDetails} />
            {carLocation?.is_car_note_enabled && (
              <Button className="-appointment-status button-note" size="small" onClick={() => setNoteType(CAR_NOTE_TYPE.Default)}>
                <Icon name="plus" color="green" size="big" />
              </Button>
            )}
          </>
        )
      }
    >
      <div className="CarNotes">
        <div className="car-notes-content">
          {defaultCarNotes.length > 0 &&
            showCardDetails &&
            defaultCarNotes.map(note => <NoteRow carNote={note} isCarNote onSelectNote={() => handleSelectCarNote(note)} key={note.id} />)}

          {dmsCarNotes.length > 0 && showCardDetails && (
            <div className="dms-remarks-container">
              <DMSNotes dmsCarNotes={dmsCarNotes} />
            </div>
          )}
        </div>

        {(noteType !== null || selectedCarNote !== null) && (
          <CarNoteModal car={car} location={carLocation} selectedNote={selectedCarNote} onUnselectNote={handleUnselectNote} onSetSelectedCarNote={handleSelectCarNote} />
        )}
      </div>
    </DetailsCard>
  );
};
